import { API, graphqlOperation } from 'aws-amplify';
import {
  listUsers,
  listDocusealTemplates,
} from '../../../../../graphql/queries';

// Types
export interface DocuSealTemplate {
  id: string;
  name: string;
  description?: string;
  url?: string;
  folder?: string;
}

export interface Viewer {
  id: string;
  type: 'user' | 'team' | 'everyone';
  name: string;
  userID?: string;
  teamID?: string;
}

export interface CRM {
  id: string;
  title: string;
}

export interface TemplateViewers {
  templateId: string;
  templateName: string;
  viewers: Viewer[];
  crms: string[]; // Array of CRM IDs, empty array means all CRMs
}

export interface User {
  id: string;
  name: string;
  email: string;
}

export interface Team {
  id: string;
  name: string;
}

// Fetch DocuSeal templates from backend
export const fetchDocuSealTemplates = async (
  orgID: string
): Promise<DocuSealTemplate[]> => {
  try {
    // console.log('DocuSeal Debug - Fetching templates for orgID:', orgID);

    const response = await API.graphql(
      graphqlOperation(listDocusealTemplates, {
        request: JSON.stringify({ orgID }),
      })
    );

    // console.log('DocuSeal Debug - Raw response:', response);
    // console.log(
    // 'DocuSeal Debug - Response data:',
    // (response as any).data.listDocusealTemplates
    // );

    const rawData = (response as any).data.listDocusealTemplates;

    if (!rawData) {
      // console.error('DocuSeal Debug - No data returned from API');
      return [];
    }

    try {
      // Parse the JSON string from the body
      const parsedData = JSON.parse(rawData);

      // console.log('DocuSeal Debug - Parsed data:', parsedData);

      if (parsedData.templates && Array.isArray(parsedData.templates)) {
        const templates = parsedData.templates.map((template: any) => ({
          id: template.id?.toString() || '',
          name: template.name || 'Untitled Template',
          description: template.description || '',
          url: template.url || '',
          folder: template.folder || 'Default',
        }));

        // console.log('DocuSeal Debug - Processed templates:', templates);
        return templates;
      } else {
        // console.error('DocuSeal Debug - Invalid data structure:', parsedData);
        return [];
      }
    } catch (parseError) {
      // console.error('DocuSeal Debug - JSON parse error:', parseError);
      // console.error('DocuSeal Debug - Raw body:', rawData.body);
      return [];
    }
  } catch (error) {
    // console.error('DocuSeal Debug - API fetch error:', error);
    throw new Error(
      `Failed to fetch DocuSeal templates: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    );
  }
};

// Fetch users and teams for viewer selection
export const fetchUsersAndTeams = async (
  orgID: string
): Promise<{ users: User[]; teams: Team[] }> => {
  try {
    // Fetch users with pagination
    let allUsers: User[] = [];
    let nextToken: string | null = null;

    do {
      const userResponse: any = await API.graphql(
        graphqlOperation(listUsers, {
          filter: { orgID: { eq: orgID }, status: { eq: 'active' } },
          limit: 1000,
          nextToken,
        })
      );

      const users = (userResponse as any).data?.listUsers?.items || [];
      const processedUsers = users.map((user: any) => ({
        id: user.id,
        name: user.name || user.email || 'Unknown User',
        email: user.email || '',
      }));

      allUsers = [...allUsers, ...processedUsers];
      nextToken = (userResponse as any).data?.listUsers?.nextToken || null;
    } while (nextToken);

    // Fetch teams (circles) with custom query
    let allTeams: Team[] = [];
    let teamNextToken: string | null = null;

    const customCircleQuery = `
      query ListCircles(
        $filter: ModelCircleFilterInput
        $limit: Int
        $nextToken: String
      ) {
        listCircles(filter: $filter, limit: $limit, nextToken: $nextToken) {
          items {
            id
            name
            isActive
          }
          nextToken
        }
      }
    `;

    do {
      const teamResponse: any = await API.graphql({
        query: customCircleQuery,
        variables: {
          filter: {
            orgID: { eq: orgID },
            isDeleted: { eq: false },
            isActive: { eq: true },
          },
          limit: 1000,
          nextToken: teamNextToken,
        },
      });

      const teams = (teamResponse as any).data?.listCircles?.items || [];
      const processedTeams = teams.map((team: any) => ({
        id: team.id,
        name: team.name || 'Unknown Team',
      }));

      allTeams = [...allTeams, ...processedTeams];
      teamNextToken =
        (teamResponse as any).data?.listCircles?.nextToken || null;
    } while (teamNextToken);

    return {
      users: allUsers,
      teams: allTeams,
    };
  } catch (error) {
    console.error('Failed to fetch users and teams:', error);
    throw new Error(
      `Failed to fetch users and teams: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    );
  }
};

// Convert templateViewers to org.docuSealAccess format
export const convertToOrgFormat = (
  templateViewers: TemplateViewers[]
): Record<string, { viewers: string[]; crms: string[] }> => {
  const docuSealAccess: Record<string, { viewers: string[]; crms: string[] }> =
    {};

  templateViewers.forEach((templateViewer) => {
    const { templateId, viewers, crms } = templateViewer;

    // Convert viewers to array of userIDs and teamIDs
    const accessList: string[] = [];
    viewers.forEach((viewer) => {
      if (viewer.type === 'user' && viewer.userID) {
        accessList.push(viewer.userID);
      } else if (viewer.type === 'team' && viewer.teamID) {
        accessList.push(viewer.teamID);
      }
    });

    docuSealAccess[templateId] = {
      viewers: accessList,
      crms: crms || [], // Empty array means all CRMs
    };
  });

  return docuSealAccess;
};

// Save DocuSeal viewers to org table
export const saveDocuSealViewers = async (
  orgID: string,
  templateViewers: TemplateViewers[]
): Promise<void> => {
  if (!orgID) {
    throw new Error('Organization ID is required to save DocuSeal viewers');
  }

  if (!Array.isArray(templateViewers)) {
    throw new Error('Invalid template viewers data');
  }

  try {
    const docuSealAccess = convertToOrgFormat(templateViewers);

    const updateOrgMutation = `
      mutation UpdateOrg($input: UpdateOrgInput!) {
        updateOrg(input: $input) {
          id
          docuSealAccess
        }
      }
    `;

    const docuSealAccessString = JSON.stringify(docuSealAccess);

    const response = await API.graphql(
      graphqlOperation(updateOrgMutation, {
        input: {
          id: orgID,
          docuSealAccess: docuSealAccessString,
        },
      })
    );

    const updatedOrg = (response as any).data?.updateOrg;

    if (!updatedOrg) {
      throw new Error('Failed to update organization - no response data');
    }

    if (!updatedOrg.docuSealAccess) {
      throw new Error(
        'Organization updated but docuSealAccess field was not set'
      );
    }

    console.log('DocuSeal viewers saved successfully:', updatedOrg);
  } catch (error) {
    console.error('Error saving DocuSeal viewers:', error);

    if (error instanceof Error) {
      if (error.message.includes('Network')) {
        throw new Error(
          'Network error while saving. Please check your connection and try again.'
        );
      }
      if (error.message.includes('not authorized')) {
        throw new Error(
          'You do not have permission to update DocuSeal settings.'
        );
      }
      throw new Error(`Failed to save DocuSeal viewers: ${error.message}`);
    }

    throw new Error('Failed to save DocuSeal viewers: Unknown error');
  }
};

// Initialize default viewers for templates
export const initializeTemplateViewers = (
  templates: DocuSealTemplate[]
): TemplateViewers[] => {
  return templates.map((template) => ({
    templateId: template.id,
    templateName: template.name,
    viewers: [], // Empty array = everyone access by default
    crms: [], // Empty array = all CRMs by default
  }));
};

// Convert stored access data back to TemplateViewers format
export const convertFromOrgFormat = async (
  templates: DocuSealTemplate[],
  accessData: Record<
    string,
    string[] | { viewers: string[]; crms: string[] }
  > | null,
  users: User[],
  teams: Team[]
): Promise<TemplateViewers[]> => {
  return templates.map((template) => {
    const templateId = template.id;
    const templateAccess = accessData?.[templateId];

    // Support both old format (array) and new format (object with viewers and crms)
    let viewersList: string[] = [];
    let crmsList: string[] = [];

    if (Array.isArray(templateAccess)) {
      // Old format: just an array of viewer IDs
      viewersList = templateAccess;
      crmsList = []; // Default to all CRMs
    } else if (templateAccess && typeof templateAccess === 'object') {
      // New format: object with viewers and crms
      viewersList = templateAccess.viewers || [];
      crmsList = templateAccess.crms || [];
    }

    // If no access list or empty, default to everyone
    if (viewersList.length === 0 && crmsList.length === 0) {
      return {
        templateId,
        templateName: template.name,
        viewers: [], // Empty array = everyone access
        crms: [], // Empty array = all CRMs
      };
    }

    // Convert IDs back to viewer objects
    const viewers: Viewer[] = [];

    viewersList.forEach((id) => {
      // Check if it's a user
      const user = users.find((u) => u.id === id);
      if (user) {
        viewers.push({
          id: `${templateId}-user-${id}`,
          type: 'user',
          name: user.name,
          userID: user.id,
        });
        return;
      }

      // Check if it's a team
      const team = teams.find((t) => t.id === id);
      if (team) {
        viewers.push({
          id: `${templateId}-team-${id}`,
          type: 'team',
          name: team.name,
          teamID: team.id,
        });
      }
    });

    return {
      templateId,
      templateName: template.name,
      viewers,
      crms: crmsList,
    };
  });
};
